import { Box, Flex, Text, Table, Th, Tbody, Tr, Td, Thead } from '@chakra-ui/react';
import { faEdit, faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  ButtonIcon,
  Checkbox,
  OutlineCard
} from '@morressier/alembic';
import React from 'react';

import { OwnerEntry } from './types';

interface OwnerSectionProps {
  owner: OwnerEntry | null;
  onAdd: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

const OwnerSection: React.FC<OwnerSectionProps> = ({
  owner,
  onAdd,
  onEdit,
  onDelete
}) => {
  return (
    <Flex flexDirection={'column'} gap={4}>
      {/* Header */}
      <Flex alignItems={'center'} justifyContent={'space-between'}>
        <Text textStyle={'headingSection'}>Owner</Text>
        <Button
          variant="secondary"
          iconLeft={<FontAwesomeIcon icon={faPlus} />}
          onClick={onAdd}
          isDisabled={owner !== null}
          disabled={owner !== null}
        >
          Add Owner
        </Button>
      </Flex>

      {/* Table or empty state */}
      {owner ? (
        <Box overflowX="auto" width="100%">
          <Table>
            <Thead>
            <Tr
              data-testId="owner-table-header"
              gap={4}
              sx={{
                '& th': {
                  textTransform: 'none',
                  color: 'black',
                  background: 'white'
                }
              }}
            >

              <Th p={4}>
                <Text textStyle="headingItem" whiteSpace="nowrap">
                  Level
                </Text>
              </Th>
              <Th p={4}>
                <Text textStyle="headingItem" whiteSpace="nowrap">
                  Role
                </Text>
              </Th>
              <Th p={4}>
                
                <Text textStyle="headingItem" whiteSpace="nowrap">
                  Abbreviation
                </Text>
              </Th>
              <Th p={4}>
                <Text textStyle="headingItem" whiteSpace="nowrap">
                  Auto Assign
                </Text>
              </Th>
              <Th p={4}>
                <Text textStyle="headingItem" whiteSpace="nowrap">
                  Max Limit
                </Text>
              </Th>
              <Th p={4} display={"none"}>
                <Text textStyle="headingItem" whiteSpace="nowrap">
                  Dec. Maker
                </Text>
              </Th>
              <Th width="100px" textAlign="center">
                <Text textStyle="headingItem" whiteSpace="nowrap">
                  Actions
                </Text>
              </Th>
            </Tr>
            </Thead>
            <Tbody>
              <Tr
                data-testId="owner-table-row"
                gap={4}
                background="white"
                alignItems="center"
                _last={{ borderBottom: 'none' }}
              >

                <Td>
                  <Text>{owner.config.roleLevel ?? '-'}</Text>
                </Td>
                <Td>
                  <Text textStyle="headingItem">{owner.config.role}</Text>
                </Td>
                <Td>
                  <Text>{owner.config.roleAbbreviation || '-'}</Text>
                </Td>
                <Td>
                  <Checkbox
                    id="owner-autoAssign"
                    name="owner-autoAssign"
                    ariaLabel="Auto assign"
                    isChecked={owner.config.autoAssign}
                    disabled
                  >
                    {' '}
                  </Checkbox>
                </Td>
                <Td>
                  <Text>{owner.config.roleMaxLimit ?? '-'}</Text>
                </Td>
                <Td display={"none"}>
                  <Checkbox
                    id="owner-isDecisionMaker"
                    name="owner-isDecisionMaker"
                    ariaLabel="Decision maker"
                    isChecked={owner.config.isDecisionMaker ?? false}
                    disabled
                  >
                    {' '}
                  </Checkbox>
                </Td>
                <Td>
                  <Flex gap={2}>
                    <ButtonIcon
                      variant="secondary"
                      size="small"
                      ariaLabel="Edit Owner"
                      icon={<FontAwesomeIcon icon={faEdit} />}
                      onClick={onEdit}
                    />
                    <ButtonIcon
                      variant="secondary"
                      size="small"
                      ariaLabel="Delete Owner"
                      icon={<FontAwesomeIcon icon={faTrash} color='red' />}
                      onClick={onDelete}
                    />
                  </Flex>
                </Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>
      ) : (
        <OutlineCard borderless paddingX={4} paddingY={6}>
          <Flex
            flexDirection={'column'}
            gap={2}
            alignItems="center"
            paddingY={8}
          >
            <Text textStyle="headingGroup">No Owner configured</Text>
            <Text textStyle="textBody">
              Click 'Add Owner' to set the owner role for this journal.
            </Text>
          </Flex>
        </OutlineCard>
      )}
    </Flex>
  );
};

export default OwnerSection;
